import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    EventModel,
    EventOption,
    EventOptionGroup,
    EventParticipantImage,
    GoalScorerHeader,
    OptionGroup2,
} from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageComponent, ParticipantImageService, ParticipantType, isPlayerMatched } from '@frontend/sports/participant-image/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { Promise as UnpatchedPromise } from '@rx-angular/cdk/zone-less/browser';
import { RxFor } from '@rx-angular/template/for';
import { groupBy, keys, sortBy } from 'lodash-es';
import { BehaviorSubject, Subject, map, startWith, switchMap, take, tap } from 'rxjs';

import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { GoalScorerBaseComponent } from './goal-scorer-base.component';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionGroupService } from './option-group.service';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';
import { BaseOptionGroupStatisticsService } from './player-statistics/base-option-group-statistics.service';
import { OptionGroupPlayerStatisticsService, StatisticsParams } from './player-statistics/option-group-player-statistics.service';
import { PlayerStatisticsComponent } from './player-statistics/player-statistics.component';
import { PlayerStatisticsService, SeasonStatisticsType } from './player-statistics/player-statistics.service';
import { PlayerStatsComponent } from './player-stats/player-stats.component';
import { TeamGroupingTabBarComponent } from './team-grouping/team-grouping-tab-bar.component';

@Component({
    selector: 'ms-goal-scorer-option-group',
    templateUrl: './goal-scorer-group.component.html',
    providers: [ParticipantImageService, OptionGroupPlayerStatisticsService, BaseOptionGroupStatisticsService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RxFor,
        NgClass,
        ParticipantImageComponent,
        MarketHelpComponent,
        TeamGroupingTabBarComponent,
        OptionComponent,
        OptionPanelBottomActionComponent,
        IconCustomComponent,
        PlayerStatisticsComponent,
        PlayerStatsComponent,
    ],
})
export class GoalScorerGroupComponent extends GoalScorerBaseComponent implements OnInit, OnChanges {
    sitecore = inject(Sitecore);
    private loggerFactory = inject(LoggerFactory);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private optionGroupService = inject(OptionGroupService);
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    private participantImageService = inject(ParticipantImageService);

    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() isShowMoreDisabled: boolean;
    @Input() showMarketHelp: boolean;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick: SourceOfPick;
    @Input() betDetails = '';
    @Input() calendarTrack?: string;
    optionsLimitOverride = input<number | undefined>();

    @HostBinding('class') get participantJersey(): string {
        if (this.isShowOptionImage) {
            return 'jersey-panel';
        }

        return '';
    }

    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    readonly optionGroupPlayerStatisticsService = inject(OptionGroupPlayerStatisticsService);
    private readonly strategyProvider = inject(RxStrategyProvider);
    readonly groupsRendered$ = new Subject<OptionGroup2[]>();
    readonly renderStrategy$ = new BehaviorSubject<string>(this.strategyProvider.primaryStrategy);
    private readonly logger: SportsRemoteLogger = this.loggerFactory.getLogger('GoalScorerGroupComponent');
    optionImages: NumberDictionary<EventParticipantImage> = {};
    enableOptionImages: boolean;
    isShowOptionImage = false;
    isShowHeadshotImage = false;
    participantType = ParticipantType;

    // transform to signal input as soon as they are available
    _optionGroup = signal<EventOptionGroup | undefined>(undefined);
    optionGroups = signal<OptionGroup2[]>([]);
    isCollapsed = signal(true);
    showAllOptions = computed(() => {
        const scoreRowsCount = this.optionGroups().length;

        return scoreRowsCount <= this.showMoreRowsThreshold();
    });

    visibleOptions = computed(() => {
        if (this.isInSheetView || this.showAllOptions() || !this.isCollapsed()) {
            return this.optionGroups();
        }

        return this.optionGroups().filter((g, i) => i < this.showMoreRowsThreshold());
    });

    headers = computed<GoalScorerHeader[] | null>(() => (this._optionGroup() ? this.getOptionHeaders(this._optionGroup()!.options) : null));
    showHeaders = computed(() => !!this.headers()?.filter((x) => x.name)?.length);
    scoreOptionsClass = computed(() => this.getScoreOptionsClass());

    private lastOptionClass?: string;

    showMoreData = computed(() => {
        const optionCount = this.optionGroups().length;

        return {
            totalCount: optionCount,
            initialVisibleCount: this.optionsLimitOverride() ?? this.showMoreRowsThreshold(),
            pageSize: optionCount,
        };
    });

    optionsToDisplay$ = toObservable(this.visibleOptions).pipe(
        switchMap((groups) => {
            // when scheduling the updates to create new options
            // the UX will suffer hard when the amount of options
            // per row changes, e.g. tab switch.
            // This is why we want to render an empty list immediately (with native strategy)
            // and re-create the list from scratch with the normal strategy
            if (this.lastOptionClass && this.scoreOptionsClass() !== this.lastOptionClass) {
                this.lastOptionClass = this.scoreOptionsClass();
                UnpatchedPromise.resolve().then(() => this.renderStrategy$.next('native'));

                return this.groupsRendered$.pipe(
                    take(1),
                    map(() => groups),
                    tap(() => this.renderStrategy$.next(this.strategyProvider.primaryStrategy)),
                    startWith([]),
                );
            }
            this.lastOptionClass = this.scoreOptionsClass();

            return new BehaviorSubject(groups);
        }),
    );

    showMoreRowsThreshold = computed(() => {
        return this.optionsLimitOverride() ?? this.teamGroupingMaxNumberOfLines ?? 6;
    });

    private teamGroupingMaxNumberOfLines = this.marketGroupingConfig.teamGroupingMaxNumberOfLines;
    private _teamGroupingTabVisibilityThreshold = this.marketGroupingConfig.teamGroupingTabVisibilityThreshold;
    isTeamGroupingEnabled = signal(false);
    controllerTabName: string;
    playerStatisticsService = inject(PlayerStatisticsService);
    readonly playerStatisticsMessages = this.playerStatisticsService.playerStatisticsMessages;
    readonly seasonStatisticsMessage = this.playerStatisticsService.seasonStatisticsMessage;
    readonly SeasonStatisticsType = SeasonStatisticsType;

    ngOnInit(): void {
        this.controllerTabName =
            this.sitecore.teamGrouping[this.optionGroup.detailedGrouping.teamGrouping! as keyof typeof this.sitecore.teamGrouping];
    }

    ngOnChanges(changes: SimpleChanges): void {
        const isTeamGroupingEnabled = this.optionGroupService.isTeamGroupingEnabled(
            this.event,
            this.optionGroup,
            this._teamGroupingTabVisibilityThreshold,
        );
        this.isTeamGroupingEnabled.set(isTeamGroupingEnabled);

        if (
            changes.optionGroup?.previousValue !== changes.optionGroup?.currentValue ||
            changes.event?.previousValue !== changes.event?.currentValue
        ) {
            this.optionGroupPlayerStatisticsService.initializePlayerStatistics(this.event, OptionGroupType.GoalScorer);
            this.optionGroupPlayerStatisticsService.fetchPlayerStatistics(this.event, this.optionGroup);
        }

        if (changes.optionGroup) {
            this._optionGroup.set(this.optionGroup);
            this.setOptionGroups(this.optionGroup);
            this.isShowHeadshotImage =
                this.participantImageService.showHeadshotImage(this.event.league.id, OptionGroupType.GoalScorer) &&
                isPlayerMatched(this.event.players, this.optionGroup.options);
            this.isShowOptionImage = this.participantImageService.showOptionsImage(this.event, this.optionGroup);
            if (this.isShowOptionImage) {
                this.getPlayerImages(this.optionGroup);
            }
            if (this.isShowHeadshotImage) {
                this.optionGroup.options.forEach((option) => {
                    if (option.image) {
                        this.optionImages[option.id] = option.image;
                    }
                });
            }
        }
        if (isTeamGroupingEnabled) {
            this.sourceOfPick = SourceOfPick.TeamGrouping;
        }
    }

    getGroupingKey(options: [...EventOption[]], key: string): string {
        return key;
    }

    groupOptions(optionGroup: EventOptionGroup): Record<string, [...EventOption[]]> {
        return groupBy(optionGroup.options, (option) => option.name);
    }

    expand(): void {
        this.isCollapsed.set(!this.isCollapsed());
        this.publishExpandedEvent();
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.isCollapsed.set(state === 'collapsed');

        this.publishExpandedEvent();
    }

    private publishExpandedEvent(): void {
        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            this.isCollapsed() ? 'collapse' : 'expand',
            this.event.participants,
            this.optionGroup.name,
            'players',
        );
    }

    private getScoreOptionsClass(): string {
        const optionsCount = this.headers()?.length ?? 1;
        switch (optionsCount) {
            case 1:
                return 'double';
            case 2:
                return 'triple';
            case 3:
                return 'quadruple';
            case 4:
                return 'quintuple';
            case 5:
                return 'sextuple';
            case 6:
                return 'septuple';
            default:
                this.logger.error(`GoalScorer Grouping Error - ${optionsCount} columns!`);

                return optionsCount === 0 ? 'double' : 'sextuple';
        }
    }

    private setOptionGroups(optionGroup: EventOptionGroup): void {
        const columnIds = this.getOptionColumnIds(optionGroup.options);
        const groups = this.groupOptions(optionGroup);
        const mappedGroups = keys(groups).map((key) => ({
            attribute: this.getGroupingKey(groups[key], key),
            options: this.getOptions(groups[key], columnIds),
        }));

        this.optionGroups.set(
            sortBy(mappedGroups, function (group: OptionGroup2): number {
                const option = group.options[0];
                if (option && option.nativePrice) {
                    return option.nativePrice.odds.toNumber();
                }

                return 9999;
            }),
        );
    }

    onTeamGroupingChange(teamGrouping: { optionGroup: EventOptionGroup; controllerTabName?: string }): void {
        this.controllerTabName = teamGrouping.controllerTabName ?? this.controllerTabName;
        this.setOptionGroups(teamGrouping.optionGroup);
    }

    getParticipantImage(options?: (EventOption | undefined)[]): EventParticipantImage | undefined {
        return options
            ?.map((option) => {
                if (option) {
                    return this.optionImages[option.id];
                }

                return undefined;
            })
            .find((image) => image);
    }

    optionTracking(option: EventOption) {
        return {
            source: this.tracking,
            calendarTitle: this.calendarTrack,
            moduleType: this.trackingModuleType,
            controllerTabName: this.controllerTabName,
            playersName: option.name,
        };
    }

    togglePlayerStatistics(group: OptionGroup2): void {
        const params = this.extractParams(group);
        this.optionGroupPlayerStatisticsService.toggleOpen(this.event, params, this.optionGroup);
    }

    isPlayerStatisticsOpen(group: OptionGroup2): boolean {
        const params = this.extractParams(group);
        return this.optionGroupPlayerStatisticsService.isOpen(params);
    }

    private extractParams(group: OptionGroup2): StatisticsParams {
        const option = group.options[0];
        const optionGroupId = option?.optionGroupId.toString();
        const optionId = option?.id?.toString();
        return {
            optionGroupId,
            playerName: group.attribute,
            key: `${option?.optionGroupId}_${option?.playerId ?? option?.player1Id}`,
            optionId,
        };
    }

    private getPlayerImages(optionGroup: EventOptionGroup): void {
        this.optionImages = {};
        const options = this.participantImageService.getPlayerJersey(this.event, optionGroup);

        options.forEach((option) => {
            this.optionImages[option.id] = this.participantImageService.getParticipantImage(
                option,
                this.event.sport?.id,
                this.event.offerSource,
                true,
            );
        });
    }
}
